import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FaClock, FaFilter, FaMapMarkerAlt, FaSearch, FaStar } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { toursAPI } from '../services/api';
import './Tours.css';

const Tours = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    minPrice: '',
    maxPrice: ''
  });
  const location = useLocation();

  // Read query params (e.g. ?category=Family%20Tour) and initialize filters from them
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const newFilters = {};

    const qSearch = params.get('search');
    const qCategory = params.get('category');
    const qMin = params.get('minPrice');
    const qMax = params.get('maxPrice');

    if (qSearch) newFilters.search = qSearch;
    if (qCategory) newFilters.category = qCategory;
    if (qMin) newFilters.minPrice = qMin;
    if (qMax) newFilters.maxPrice = qMax;

    // Only update filters if any query param was present
    if (Object.keys(newFilters).length > 0) {
      setFilters(prev => ({ ...prev, ...newFilters }));
    }
    // If no params, do nothing here — the existing fetch effect (below) will run on mount
  }, [location.search]);

  // When navigated with a category query param, scroll to the tours section
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('category')) {
      // small timeout to allow render/mount
      setTimeout(() => {
        const el = document.getElementById('tours');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    }
  }, [location.search]);

  useEffect(() => {
    fetchTours();
  }, [filters]);

  const fetchTours = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.category !== 'all') params.category = filters.category;
      if (filters.minPrice) params.minPrice = filters.minPrice;
      if (filters.maxPrice) params.maxPrice = filters.maxPrice;

      const response = await toursAPI.getAll(params);
      setTours(response.data.data);
    } catch (error) {
      console.error('Error fetching tours:', error);
      setError('Unable to load tours. The server might be waking up. Please wait a moment and refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchTours();
  };

  return (
    <div className="tours-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="container">
          <h1 className="page-title">Explore Our Tours</h1>
          <p className="page-subtitle">Find your perfect adventure from our curated collection</p>
        </div>
      </div>

  <div id="tours" className="container section">
        {/* Search & Filters */}
        <div className="filters-section">
          <form onSubmit={handleSearchSubmit} className="search-form">
            <div className="search-box">
              <FaSearch className="search-icon" />
              <input
                type="text"
                name="search"
                placeholder="Search destinations, tours..."
                value={filters.search}
                onChange={handleFilterChange}
                className="search-input"
              />
            </div>
          </form>

          <div className="filters">
            <div className="filter-group">
              <label><FaFilter /> Category</label>
              <select name="category" value={filters.category} onChange={handleFilterChange} className="filter-select">
                <option value="all">All Categories</option>
                <option value="Domestic Tours">Domestic Tours</option>
                <option value="International Tours">International Tours</option>
                <option value="Inbound and Outbound Tours">Inbound and Outbound Tours</option>
                <option value="Family Tours">Family Tours</option>
                <option value="Group Tours">Group Tours</option>
                <option value="Honeymoon Couple Tours">Honeymoon Couple Tours</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Min Price</label>
              <input
                type="number"
                name="minPrice"
                placeholder="₹0"
                value={filters.minPrice}
                onChange={handleFilterChange}
                className="filter-input"
              />
            </div>

            <div className="filter-group">
              <label>Max Price</label>
              <input
                type="number"
                name="maxPrice"
                placeholder="₹10000"
                value={filters.maxPrice}
                onChange={handleFilterChange}
                className="filter-input"
              />
            </div>
          </div>
        </div>

        {/* Tours Grid */}
        {loading ? (
          <div className="spinner"></div>
        ) : tours.length > 0 ? (
          <>
            {/* results count intentionally hidden per user request */}
            <div className="tours-grid grid grid-3">
              {tours.map((tour) => (
                <div key={tour._id} className="tour-card card fade-in">
                  <div className="tour-image-wrapper">
                    <img
                      src={tour.images?.[0] || `https://images.unsplash.com/photo-${Math.random() > 0.5 ? '1488646953014' : '1469854523086'}-85cb44e25828?w=500`}
                      alt={tour.title}
                      className="card-image"
                    />
                    <span className="tour-badge badge badge-primary">{tour.category}</span>
                  </div>
                  <div className="card-body">
                    <h3 className="tour-title">{tour.title}</h3>
                    <div className="tour-info">
                      <span className="info-item">
                        <FaMapMarkerAlt /> {tour.destination}
                      </span>
                      <span className="info-item">
                        <FaClock /> {tour.duration}
                      </span>
                    </div>
                    <p className="tour-description">
                      {tour.description.substring(0, 100)}...
                    </p>
                    <div className="tour-footer">
                      <div className="tour-rating">
                        <FaStar className="star-icon" />
                        <span>{tour.rating || 5.0}</span>
                      </div>
                      <div className="tour-price">
                        <span className="price-label">Starting from</span>
                        <span className="price-value">₹{tour.price}</span>
                      </div>
                    </div>
                    <Link to={`/tours/${tour._id}`} className="btn btn-primary" style={{ width: '100%', marginTop: '15px' }}>
                      View Details
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : error ? (
          <div className="no-results error-message">
            <h3>⚠️ Connection Issue</h3>
            <p>{error}</p>
            <button onClick={fetchTours} className="btn btn-primary" style={{ marginTop: '20px' }}>
              Retry Connection
            </button>
          </div>
        ) : (
          <div className="no-results">
            {/* If user navigated by category and there are no tours, show a contact CTA instead of plain "No tours found" */}
            {filters.category && filters.category !== 'all' ? (
              <>
                <h3>No tours found for "{filters.category}"</h3>
                <p>
                  We don't have packages listed for this category right now. Contact us to know more or to request a custom
                  itinerary — we'd be happy to help.
                </p>
                <Link to="/contact" className="btn btn-primary" style={{ marginTop: '20px' }}>
                  Contact Us
                </Link>
              </>
            ) : (
              <>
                <h3>No tours found</h3>
                <p>Try adjusting your filters or search terms</p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Tours;
